import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Clock, 
  Users, 
  Star, 
  Heart, 
  Phone,
  MessageCircle,
  Crown,
  Shield,
  Globe,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';

const ConciergePage = () => {
  const services = [
    {
      category: "Voyage & Transport",
      icon: Globe,
      services: [
        "Réservation jets privés et yachts",
        "Transferts VIP avec chauffeur",
        "Hélicoptères et transport premium",
        "Réservations restaurants étoilés",
        "Accès événements exclusifs",
        "Guides privés experts locaux"
      ]
    },
    {
      category: "Bien-être & Lifestyle",
      icon: Sparkles,
      services: [
        "Spa et soins à domicile",
        "Chef privé et service traiteur",
        "Personal trainer et cours privés",
        "Shopping personnel et livraisons",
        "Garde d'enfants qualifiée",
        "Soins vétérinaires pour animaux"
      ]
    },
    {
      category: "Expériences Exclusives",
      icon: Crown,
      services: [
        "Accès lieux privés fermés au public",
        "Rencontres avec personnalités",
        "Concerts et spectacles privés",
        "Ateliers avec artistes renommés",
        "Expériences gastronomiques uniques",
        "Aventures sur-mesure impossibles"
      ]
    },
    {
      category: "Support & Urgences",
      icon: Shield,
      services: [
        "Assistance médicale 24/7",
        "Support technique informatique",
        "Résolution problèmes urgents",
        "Coordination avec services locaux",
        "Traduction et interprétariat",
        "Assistance administrative"
      ]
    }
  ];

  const conciergeProfiles = [
    {
      name: "Sophie",
      specialty: "Europe & Gastronomie",
      experience: "12 ans",
      languages: ["Français", "Anglais", "Italien", "Espagnol"],
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
      achievements: "Ancienne sommelier palace parisien",
      highlight: "Réseau exclusif chefs étoilés Michelin"
    },
    {
      name: "Marcus",
      specialty: "Caraïbes & Aventure",
      experience: "8 ans",
      languages: ["Anglais", "Français", "Espagnol"],
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
      achievements: "Expert sports nautiques de luxe",
      highlight: "Accès exclusif îles privées"
    },
    {
      name: "Akiko",
      specialty: "Bien-être & Spiritualité",
      experience: "15 ans",
      languages: ["Japonais", "Anglais", "Français"],
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
      achievements: "Maître reiki certifiée",
      highlight: "Réseau international spas luxury"
    }
  ];

  const responseStats = [
    { metric: "< 5 min", description: "Temps de réponse moyen" },
    { metric: "24/7", description: "Disponibilité garantie" },
    { metric: "99.8%", description: "Taux de résolution" },
    { metric: "12", description: "Langues parlées" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2080&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Clock className="w-4 h-4 mr-2" />
            Service Premium
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Conciergerie 24/7
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Votre équipe d'experts dédiés, disponible à tout moment pour transformer vos souhaits en réalité
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              Appeler Maintenant
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-800 px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Chat en Direct
            </Button>
          </div>
        </div>
      </section>

      {/* Response Stats */}
      <section className="py-16 px-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {responseStats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-light mb-2">{stat.metric}</div>
                <p className="text-amber-100 text-sm uppercase tracking-wide">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Categories */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Services à votre Disposition
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              De la simple réservation à l'expérience la plus exclusive, nous réalisons l'impossible
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <h3 
                      className="text-2xl font-light text-gray-800"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {category.category}
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    {category.services.map((service, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{service}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Concierge Team */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Votre Équipe d'Experts
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Rencontrez quelques-uns de nos concierges d'exception, choisis pour leur expertise unique
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {conciergeProfiles.map((concierge, index) => (
              <Card key={index} className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-white">
                <div className="text-center mb-6">
                  <img
                    src={concierge.avatar}
                    alt={concierge.name}
                    className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-4 border-white/20"
                  />
                  <h3 
                    className="text-xl font-light mb-1"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {concierge.name}
                  </h3>
                  <p className="text-amber-400 text-sm font-medium mb-2">
                    {concierge.specialty}
                  </p>
                  <p className="text-white/70 text-xs mb-4">
                    {concierge.experience} d'expérience
                  </p>
                </div>
                
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wide mb-2">Langues</p>
                    <div className="flex flex-wrap gap-1">
                      {concierge.languages.map((lang, idx) => (
                        <Badge key={idx} variant="outline" className="border-white/30 text-white text-xs">
                          {lang}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wide mb-2">Expertise</p>
                    <p className="text-white/90 text-xs">{concierge.achievements}</p>
                  </div>
                  
                  <div className="pt-4 border-t border-white/20">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-400" />
                      <p className="text-amber-400 text-xs font-medium">{concierge.highlight}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Comment ça Marche
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Un processus simple pour des résultats extraordinaires
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-light text-white">1</span>
              </div>
              <h3 
                className="text-xl font-light text-gray-800 mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Vous nous Contactez
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Par téléphone, chat ou email, 24h/24 et 7j/7. Votre concierge dédié comprend immédiatement vos besoins.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-light text-white">2</span>
              </div>
              <h3 
                className="text-xl font-light text-gray-800 mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Nous Organisons
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Grâce à notre réseau exclusif et notre expertise, nous créons la solution parfaite adaptée à votre demande.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-light text-white">3</span>
              </div>
              <h3 
                className="text-xl font-light text-gray-800 mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Vous Profitez
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Détendez-vous pendant que nous nous occupons de tous les détails. Votre seule préoccupation : savourer l'instant.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Prêt à Vivre l'Impossible ?
          </h2>
          <p 
            className="text-xl mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Nos concierges sont en ligne maintenant, prêts à exaucer vos souhaits les plus ambitieux
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              Contacter un Concierge
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
            >
              Voir nos Services
            </Button>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4 text-amber-600" />
              <span>Réponse immédiate garantie</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4 text-amber-600" />
              <span>Aucune demande trop complexe</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4 text-amber-600" />
              <span>Service gratuit pour nos membres</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConciergePage;